import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { NEWSROOM_RETURN, newsroomDetailState } from "@/lib/newsroomNavigation";
import { publicApi } from "@/lib/api";
import { getPostThumbnailUrl } from "@/lib/html";
import type { NewsItem } from "@/lib/types";

const HOME_NEWS_MAX_ITEMS = 3;

const newsCardClass =
  "flex flex-1 flex-col min-h-0 rounded-xl border border-gray-100 bg-[#f6f9fc] p-4 transition-all duration-300 hover:border-[#1977cc]/30 hover:shadow-sm hover:bg-[#f1f7fc]";

export default function BioconNews() {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await publicApi.get<NewsItem[]>("/public/news/news");
      setItems((res.data || []).slice(0, HOME_NEWS_MAX_ITEMS));
    } catch {
      // 데이터 없을 경우 빈 목록 유지
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return (
    <div className="w-full h-full flex flex-col bg-white rounded-xl p-6 md:p-8 border border-gray-100 hover:border-[#1977cc]/30 transition-colors">
      <div className="mb-6 shrink-0">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-1 h-5 bg-[#1977cc] rounded-full" />
          <h2 className="text-lg font-bold text-[#2c4964]">Biocon News</h2>
        </div>
        <div className="h-px bg-gray-100" />
      </div>

      <div className="flex flex-col flex-1 gap-4 min-h-0">
        {loading ? (
          Array.from({ length: HOME_NEWS_MAX_ITEMS }).map((_, i) => (
            <div key={i} className={`${newsCardClass} animate-pulse`}>
              <div className="flex-1 min-h-[100px] bg-gray-100 rounded-lg mb-3" />
              <div className="shrink-0 space-y-2">
                <div className="h-4 bg-gray-100 rounded w-full" />
                <div className="h-3 bg-gray-100 rounded w-2/3" />
                <div className="h-3 bg-gray-100 rounded w-full" />
              </div>
            </div>
          ))
        ) : (
          items.map((item, index) => (
            <motion.article
              key={item.id}
              className={`group ${newsCardClass}`}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{
                duration: 0.5,
                delay: index * 0.1,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              <Link
                to={`/newsroom/news/${item.id}`}
                state={newsroomDetailState(NEWSROOM_RETURN.home)}
                className="block flex-1 min-h-0 mb-3 overflow-hidden rounded-lg bg-white ring-1 ring-gray-100 group-hover:ring-[#1977cc]/30 transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-full min-h-[100px]">
                  <img
                    src={getPostThumbnailUrl(item.imageUrl, item.content)}
                    alt={item.title}
                    className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-[#2c4964]/0 group-hover:bg-[#1977cc]/15 transition-colors duration-300">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-white/90 text-[#1977cc] opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                      +
                    </span>
                  </div>
                </div>
              </Link>

              <div className="shrink-0">
                <Link
                  to={`/newsroom/news/${item.id}`}
                  state={newsroomDetailState(NEWSROOM_RETURN.home)}
                  className="cursor-pointer"
                >
                  <h3 className="text-sm font-semibold text-[#2c4964] group-hover:text-[#1977cc] transition-colors leading-snug mb-2">
                    {item.title}
                  </h3>
                </Link>

                <div className="text-xs text-gray-400 mb-2">
                  <span className="text-[#1977cc] font-medium">{item.author}</span>, {item.date}, News, Notice
                </div>

                <p className="text-sm text-gray-500 leading-relaxed line-clamp-3">
                  {item.excerpt}
                </p>
              </div>
            </motion.article>
          ))
        )}
      </div>
    </div>
  );
}
